import './styles/_shopable-scroll.scss';

// eslint-disable-next-line import/no-unresolved
import Swiper from 'swiper-latest/bundle';

import {
  addUniqueActiveClass,
  checkIsMobile,
  isScriptFullyLoaded,
  markScriptFullyLoaded,
  onDocumentReady,
} from '../utils/dom';

import { initSwipers } from '../utils/swiper';
import { checkWindowSize } from '../utils/performance';

const options = {
  root: null,
  rootMargin: '0px',
  threshold: 0.01,
};

const state = {
  variables: {
    title: 'shopable-scroll',
    isMobile: window.innerWidth <= 749,
  },
  elements: {},
};

let userComeAcrossWithSection = false; 

const cacheState = () => {
  state.elements = {
    collectionSections: document.querySelectorAll('[data-collection]'),
    looksSections: document.querySelectorAll('[data-looks]'),
    actions: document.querySelectorAll('[data-action]')
  };
  state.variables.isMobile = checkIsMobile();
  const sections = Array.from(document.querySelectorAll('.container[data-id]'));
  state.components = sections.reduce((acc, curr) => {
    const currentComponent = {
      swiperEl: curr.querySelector('.swiper.shopable-scroll-section__look'),
      totalSlides: Array.from(curr.querySelectorAll('.swiper-slide')).length,
      banners: Array.from(curr.querySelectorAll('.swiper-slide')),
    };
    return [...acc, currentComponent];
  }, []);
};

function initVideo() {
  state.components?.forEach((component) => {
    const { banners } = component;

    banners?.forEach((banner) => {
      const videoEl = banner.querySelector('video');
      if (!videoEl) return;

      let wasPlaying = true;
      let wasMuted = true;
      const playPauseBtn = banner.querySelector('[data-type="video"]');
      const muteUnmuteBtn = banner.querySelector('[data-type="audio"]');

      playPauseBtn.addEventListener('click', () => {
        if (playPauseBtn.dataset.state === 'playing') {
          videoEl.pause();
          playPauseBtn.dataset.state = 'paused';
        } else {
          videoEl.play();
          playPauseBtn.dataset.state = 'playing';
        }
      });

      muteUnmuteBtn.addEventListener('click', () => {
        if (muteUnmuteBtn.dataset.state === 'muted') {
          videoEl.muted = false;
          muteUnmuteBtn.dataset.state = 'unmuted';
        } else {
          videoEl.muted = true;
          muteUnmuteBtn.dataset.state = 'muted';
        }
      });

      document.addEventListener('swiper:slide:changed', () => {
        if (playPauseBtn.dataset.state === 'playing') {
          videoEl.pause();
          playPauseBtn.dataset.state = 'paused';
        } else if (
          videoEl.closest('.slider__item').classList.contains('active')
        ) {
          videoEl.play();
          playPauseBtn.dataset.state = 'playing';
        }
      });

      const observer = new IntersectionObserver((e) => {
        if (!e[0].isIntersecting) {
          if (userComeAcrossWithSection) {
            wasPlaying = !videoEl.paused;
          }
          wasMuted = videoEl.muted;

          videoEl.pause();
          playPauseBtn.dataset.state = 'paused';
          return;
        }

        videoEl.muted = wasMuted;
        userComeAcrossWithSection = true;
        if (!wasPlaying) return;

        videoEl.play();
        playPauseBtn.dataset.state = 'playing';
      }, options);
      observer.observe(videoEl);
    });
  });
}

const initializeSwiper = (enabled, el) =>
  new Swiper(el, {
    spaceBetween: 0,
    enabled,
    lazy: true,
    navigation: {
      nextEl: el.querySelector('.swiper-button-next'),
      prevEl: el.querySelector('.swiper-button-prev'),
    },
  });

const initProductSwiper = (el, disableOnMobile = false) => {
  let swiper = initializeSwiper(
    !disableOnMobile || !state.variables.isMobile,
    el
  );

  const handleResize = (e) => {
    const { isMobile } = e.detail;

    if (isMobile && swiper) {
      swiper.destroy(true, true);
      swiper = null;
    } else if (!isMobile && !swiper) {
      swiper = initializeSwiper(!isMobile, el);
    }
  };

  if (disableOnMobile) {
    document.addEventListener('resize::update', handleResize);
  }
};

const initCollection = () => {
  initSwipers(state.elements.collectionSections, (swiperEl) => {
    initProductSwiper(swiperEl, true);
  });
};

const initLooks = () => {
  initSwipers(state.elements.looksSections, initProductSwiper);
};

const onMobileLookButtonClick = (target) => {
  const index = target?.getAttribute('data-look-key');
  const parent = target?.closest('.shopable-scroll-section');
  const lookKeys = parent?.querySelectorAll('[data-look-key]');
  const swiperToScroll = parent?.querySelector(`[data-swiper="${index}"]`);

  addUniqueActiveClass(lookKeys, index, 'active');
  swiperToScroll?.scrollIntoView({
    behavior: 'smooth',
    inline: 'center',
    block: 'nearest',
  });
};

const onSectionCTAClick = (el) => {
  const header = document.querySelector('sticky-header');
  const target = el.getAttribute('data-action');
  const parent = el.closest('.shopable-scroll-section');
  const targetEl = parent.querySelector(`[data-${target}]`);
  const isMobile = checkIsMobile();

  if (isMobile) {
    const headerHeight = +header.getBoundingClientRect().height;
    const announcementBarHeight = document
      .querySelector(
        '[id^="shopify-section-sections--"][id$="__announcement-bar"]'
      )
      .getBoundingClientRect().height;
    const y =
      targetEl.getBoundingClientRect().top +
      window.scrollY -
      headerHeight -
      announcementBarHeight;
    window.scrollTo({ top: y, left: 0, behavior: 'smooth' });
    return;
  }

  targetEl.scrollIntoView({
    behavior: 'smooth',
    inline: 'center',
    block: 'nearest',
  });
};

const addEventListeners = () => {
  state.elements.actions.forEach((el) => {
    el.addEventListener('click', () => onSectionCTAClick(el));
  });
  document.addEventListener('click', (e) => {
    const { target } = e;
    if (!target?.closest('[data-look-key]')) return;

    onMobileLookButtonClick(target);
  });
  window.addEventListener('resize', checkWindowSize);
};

const init = () => {
  if (isScriptFullyLoaded(state.variables.title)) return;
  markScriptFullyLoaded(state.variables.title);

  cacheState();
  initCollection();
  initLooks();
  addEventListeners();
  initVideo();
};

onDocumentReady(init);
